<template>
  <div class="transaction-history p-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold text-gray-900">{{ $t('loyalty.title.transactions') }}</h3>
      <div class="flex items-center space-x-3">
        <el-select
          v-model="filters.type"
          :placeholder="$t('loyalty.label.transaction_type')"
          clearable
          size="small"
          style="width: 180px;"
          @change="fetchTransactions">
          <el-option :label="$t('label.all')" value=""></el-option>
          <el-option :label="$t('loyalty.transaction_type.earn')" value="earn"></el-option>
          <el-option :label="$t('loyalty.transaction_type.burn')" value="burn"></el-option>
          <el-option :label="$t('loyalty.transaction_type.adjust')" value="adjust"></el-option>
          <el-option :label="$t('loyalty.transaction_type.expire')" value="expire"></el-option>
        </el-select>
        <button
          @click="fetchTransactions"
          class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
          <i class="mdi mdi-refresh mr-1.5"></i>
          {{ $t('button.refresh') }}
        </button>
      </div>
    </div>

    <div v-loading="loading">
      <div v-if="transactions.length === 0" class="text-center py-12">
        <i class="mdi mdi-information-outline text-4xl text-gray-400 mb-2"></i>
        <p class="text-gray-500">{{ $t('message.no_data') }}</p>
      </div>
      <div v-else class="space-y-4">
        <div
          v-for="transaction in filteredTransactions"
          :key="transaction.id"
          class="bg-white border-2 rounded-xl p-6 hover:shadow-lg transition-all duration-200"
          :class="getTransactionBorderClass(transaction.type)">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center space-x-3 mb-3">
                <div
                  :class="getTransactionIconClass(transaction.type)"
                  class="w-12 h-12 rounded-lg flex items-center justify-center">
                  <i :class="getTransactionIcon(transaction.type)" class="text-2xl text-white"></i>
                </div>
                <div>
                  <h4 class="text-lg font-bold text-gray-900">{{ transaction.description || $t('loyalty.transaction_type.' + transaction.type) }}</h4>
                  <p class="text-sm text-gray-500">{{ $shortDateTime(transaction.created_at) }}</p>
                </div>
                <span
                  :class="getTransactionBadgeClass(transaction.type)"
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium">
                  {{ $t('loyalty.transaction_type.' + transaction.type) }}
                </span>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div class="bg-gray-50 rounded-lg p-3">
                  <p class="text-xs text-gray-500 mb-1">{{ $t('loyalty.label.points') }}</p>
                  <p
                    :class="getTransactionPointsClass(transaction.type)"
                    class="text-xl font-bold">
                    {{ getTransactionPointsPrefix(transaction.type) }}{{ Math.abs(transaction.points).toLocaleString() }}
                  </p>
                </div>
                <div class="bg-gray-50 rounded-lg p-3">
                  <p class="text-xs text-gray-500 mb-1">{{ $t('loyalty.label.balance_after') }}</p>
                  <p class="text-lg font-semibold text-gray-900">{{ transaction.balance_after.toLocaleString() }}</p>
                </div>
                <div v-if="transaction.expires_at" class="bg-gray-50 rounded-lg p-3">
                  <p class="text-xs text-gray-500 mb-1">{{ $t('loyalty.label.expires_at') }}</p>
                  <p class="text-sm font-medium text-gray-900">{{ $shortDate(transaction.expires_at) }}</p>
                </div>
              </div>
              <div v-if="transaction.reference_type" class="mt-3 text-xs text-gray-500">
                {{ $t('loyalty.label.reference') }}: {{ transaction.reference_type }} #{{ transaction.reference_id }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'TransactionHistory',
  mixins: [ErrorHandlingMixin],
  props: {
    customerId: {
      type: [Number, String],
      required: true
    },
    programId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      transactions: [],
      loading: false,
      filters: {
        type: ''
      }
    }
  },
  computed: {
    filteredTransactions () {
      if (!this.filters.type) {
        return this.transactions
      }
      return this.transactions.filter(t => t.type === this.filters.type)
    }
  },
  async mounted () {
    await this.fetchTransactions()
  },
  watch: {
    programId: {
      immediate: true,
      handler () {
        this.fetchTransactions()
      }
    }
  },
  methods: {
    async fetchTransactions () {
      try {
        this.loading = true
        this.transactions = await this.$store.dispatch('loyalty/customer/fetchTransactions', {
          customerId: this.customerId,
          programId: this.programId,
          limit: 100
        })
      } catch (error) {
        console.error('Error fetching transactions:', error)
        this.globalErrHandle(error)
      } finally {
        this.loading = false
      }
    },
    getTransactionIcon (type) {
      const icons = {
        earn: 'mdi-plus-circle',
        burn: 'mdi-minus-circle',
        adjust: 'mdi-pencil-circle',
        expire: 'mdi-clock-alert-outline',
        refund: 'mdi-refresh'
      }
      return icons[type] || 'mdi-circle'
    },
    getTransactionIconClass (type) {
      const classes = {
        earn: 'bg-green-500',
        burn: 'bg-red-500',
        adjust: 'bg-blue-500',
        expire: 'bg-orange-500',
        refund: 'bg-purple-500'
      }
      return classes[type] || 'bg-gray-500'
    },
    getTransactionBorderClass (type) {
      const classes = {
        earn: 'border-green-300',
        burn: 'border-red-300',
        adjust: 'border-blue-300',
        expire: 'border-orange-300',
        refund: 'border-purple-300'
      }
      return classes[type] || 'border-gray-200'
    },
    getTransactionBadgeClass (type) {
      const classes = {
        earn: 'bg-green-100 text-green-800',
        burn: 'bg-red-100 text-red-800',
        adjust: 'bg-blue-100 text-blue-800',
        expire: 'bg-orange-100 text-orange-800',
        refund: 'bg-purple-100 text-purple-800'
      }
      return classes[type] || 'bg-gray-100 text-gray-800'
    },
    getTransactionPointsClass (type) {
      const classes = {
        earn: 'text-green-600',
        burn: 'text-red-600',
        adjust: 'text-blue-600',
        expire: 'text-orange-600',
        refund: 'text-purple-600'
      }
      return classes[type] || 'text-gray-600'
    },
    getTransactionPointsPrefix (type) {
      return type === 'earn' || type === 'adjust' ? '+' : '-'
    }
  }
}
</script>

<style scoped>
</style>
